//
// Respond to a customer's invite for a new 3rd party partner
//
// Public page: the piid in the link is the only credential. It fetches the
// invite, decides whether there is anything left to answer, and hands a live
// one to the acceptance wizard.
//
import {
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import {
  useSearchParams,
} from 'react-router-dom';
import superagent from 'superagent';

import {
  Button,
  Column,
  LoadingPage,
  Row,
} from '../../components';
import SupportLink from '../../components/SupportLink';
import {
  useAuth,
  useTownAssets,
} from '../../stores';
import {
  config,
  logError,
} from '../../utils';

import InviteAckWizard from './components/InviteAckWizard';

const pageClassName = 'h-screen w-screen max-h-screen max-w-screen p-2 absolute l-0 '
  + 'overflow-hidden flex flex-col items-center justify-center font-light '
  + 'text-start text-base bg-white';

const initialState = {
  status: 'init',
  invite: null,
  logoFailed: false,
  // Set once the account exists: closing the wizard then lands on the end
  // state, never back on a wizard the invite can no longer feed
  accepted: false,
  acceptedEmail: undefined,
  // The portal of the company the invitee confirmed here, where its account will live
  confirmedWww: undefined,
};

// The invite belongs to a company living on another server: it goes on there.
// Asked once only, so a portal misreporting its own address cannot loop.
const otherPortalOf = (invite, alreadyRedirected) => {
  const cstmrWww = invite?.prtnrInvtInfo?.target?.cstmrWww;
  if (alreadyRedirected || (!invite?.prtnrInvtTrgtCuid) || (!cstmrWww)) return undefined;

  let origin;
  try {
    origin = new URL(cstmrWww).origin;
  } catch {
    return undefined;
  }

  return (origin === window.location.origin) ? undefined : cstmrWww;
};

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'fetching':
      return {
        ...state,
        status: 'fetching',
      };

    case 'statusError':
      return {
        ...state,
        status: 'statusError',
      };

    case 'logoFailed':
      return {
        ...state,
        logoFailed: true,
      };

    case 'reopen':
      return {
        ...state,
        status: 'ready',
      };

    case 'accepted':
      return {
        ...state,
        accepted: true,
        acceptedEmail: action.email,
      };

    case 'refused':
      return {
        ...state,
        status: 'refused',
      };

    case 'companyConfirmed':
      return {
        ...state,
        confirmedWww: action.cstmrWww,
      };

    case 'redirecting':
      return {
        ...state,
        status: 'redirecting',
      };

    case 'dismissed':
      return {
        ...state,
        status: (state.accepted) ? 'accepted' : 'dismissed',
      };

    case 'invite': {
      // A consumed invite must not re-open the wizard: an assigned (or paused)
      // partnership lands on the "already assigned" screen, a canceled one on
      // the status error screen. Only live invites reach the configuration steps.
      const inviteStatus = action.invite?.prtnrInvtInfo?.status;
      let status = 'ready';
      if ((inviteStatus === 'assigned') || (inviteStatus === 'paused')) {
        status = 'alreadyAssigned';
      } else if (inviteStatus === 'canceled') {
        status = 'statusError';
      } else if (inviteStatus === 'refused') {
        status = 'refused';
      }

      return {
        ...state,
        invite: action.invite,
        status,
      };
    }

    default:
      logError(
        `InviteAckPage stateReducer unknown type=${action?.type}`,
        'InviteAckPage.reducer.unknownAction',
        { actionType: action?.type },
      );
      return state;
  }
};

function InviteAckPage() {
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const { assets } = useTownAssets();
  const {
    translate,
    language,
  } = assets;

  const { invite } = state;
  const { auth } = useAuth(false);
  const uidRegex = auth?.server?.regex?.uid;
  // The id comes from an emailed link a mail client may have mangled: only a
  // well-formed one is presented to the server
  const piid = (uidRegex && uidRegex.test(searchParams.get('i') ?? ''))
    ? searchParams.get('i')
    : null;

  useEffect(
    () => {
      const getStatus = async () => {
        dispatch({ type: 'fetching' });

        const url = config.town.back.urlPrefix
                  + config.town.back.invite.status;

        let rslt;

        try {
          rslt = await superagent
            .post(url)
            .set('Content-Type', 'application/json')
            .send({ piid });
        } catch (err) {
          logError(
            `getStatus ${err?.message ?? err}`,
            'InviteAckPage.getStatus.error',
            {
              err: err?.message,
              ...(err?.status && { status: err.status }),
              piid,
            },
          );
          dispatch({ type: 'statusError' });
          return;
        }

        if (rslt?.body?.rslt?.data?.[0]) {
          dispatch({
            type: 'invite',
            invite: rslt.body.rslt.data[0],
          });
        } else {
          dispatch({ type: 'statusError' });
        }
      };

      // Use void to make it clear we do not want to wait this async function:
      if (piid && (state.status === 'init')) void getStatus();
    },
    [piid, state.status],
  );

  const alreadyRedirected = Boolean(searchParams.get('r'));

  useEffect(
    () => {
      const otherPortal = otherPortalOf(invite, alreadyRedirected);

      if (otherPortal && (state.status === 'ready')) {
        dispatch({ type: 'redirecting' });
        window.location.assign(`${otherPortal}/invite?i=${piid}&r=1`);
      }
    },
    [invite, alreadyRedirected, piid, state.status],
  );

  // Answers whether the invitee leaves for another portal, as the wizard then stops
  const onCompanyConfirmed = useCallback(
    (cstmrWww) => {
      dispatch({
        type: 'companyConfirmed',
        cstmrWww,
      });

      let origin;
      try {
        origin = new URL(cstmrWww).origin;
      } catch {
        return false;
      }
      if (origin === window.location.origin) return false;

      dispatch({ type: 'redirecting' });
      window.location.assign(`${cstmrWww}/invite?i=${piid}&r=1`);

      return true;
    },
    [piid],
  );

  const onRefused = useCallback(() => dispatch({ type: 'refused' }), []);

  if ((!language?.rev) || (!uidRegex) || (state.status === 'redirecting')) return (<LoadingPage />);

  const logoSrc = invite?.prtnrInvtInfo?.source?.wideLogoId
               || invite?.prtnrInvtInfo?.source?.logoId;

  const logo = (
    <Row className="h-24 w-full max-w-96 overflow-hidden shrink-0 p-4">
      {(logoSrc && (!state.logoFailed) && (invite?.prtnrInvtSrcCuid))
        ? (
            <img
              src={`${config.town.back.urlPrefix
                + config.town.back.logos.get
                + invite.prtnrInvtSrcCuid
              }/${logoSrc}`}
              alt="logo"
              className="h-full w-full object-contain"
              onError={() => dispatch({ type: 'logoFailed' })}
            />
          )
        : (
            <img
              src="/images/png/logo.png"
              alt="paxity"
              className="h-full w-full object-contain"
            />
          )}
    </Row>
  );

  if (!piid) {
    return (
      <main className={pageClassName}>
        <Column>
          {logo}
          <span className="mb-4">{translate('unknownUrl')}</span>
          <SupportLink />
        </Column>
      </main>
    );
  }

  //
  // The account exists and its credentials are on their way; the only way on is
  // the portal it belongs to.
  //
  if (state.status === 'accepted') {
    return (
      <main className={pageClassName}>
        <Column gap={6} className="max-w-140 w-full">
          {logo}
          <span className="text-center">
            {translate('partnerAccountCreated', 'statements')}
          </span>
          <span className="text-center">
            {translate('credentialsSentTo', 'statements', { email: state.acceptedEmail })}
          </span>
          <Button
            label={translate('goToLogin')}
            onClick={() => {
              window.location.assign(`${state.confirmedWww ?? invite?.prtnrInvtInfo?.target?.cstmrWww}/login`);
            }}
            imgSrc="/images/svg/gui/next.svg"
            imgRight
            baseColor
          />
        </Column>
      </main>
    );
  }

  if (state.status === 'refused') {
    return (
      <main className={pageClassName}>
        <Column gap={6} className="max-w-140 w-full">
          {logo}
          <span className="text-center">
            {translate(
              'inviteRefused',
              'statements',
              { inviter: invite?.prtnrInvtInfo?.source?.cstmrLbl },
            )}
          </span>
        </Column>
      </main>
    );
  }

  if (['alreadyAssigned', 'statusError'].includes(state.status)) {
    return (
      <main className={pageClassName}>
        <Column gap={6} className="max-w-140 w-full">
          {logo}
          <span className="text-center">
            {translate(
              (state.status === 'alreadyAssigned') ? 'inviteAlreadyAssigned' : 'inviteStatusError',
              'statements',
            )}
          </span>
          {(state.status === 'statusError') ? <SupportLink /> : null}
        </Column>
      </main>
    );
  }

  //
  // Closing the wizard abandons nothing: the link stays good, so the page says
  // so and offers the way back in.
  //
  if (state.status === 'dismissed') {
    return (
      <main className={pageClassName}>
        <Column gap={6} className="max-w-140 w-full">
          {logo}
          <span className="text-center">
            {translate('inviteStillOpen', 'statements')}
          </span>
          <Button
            label={translate('resume')}
            onClick={() => dispatch({ type: 'reopen' })}
            imgSrc="/images/svg/gui/next.svg"
            imgRight
            baseColor
          />
          <SupportLink />
        </Column>
      </main>
    );
  }

  return (
    <main className={pageClassName}>
      <Column gap={6} className="max-w-140 w-full">
        {logo}
      </Column>

      {(state.status === 'ready')
        ? (
            <InviteAckWizard
              invite={invite}
              piid={piid}
              onAccepted={(email) => dispatch({
                type: 'accepted',
                email,
              })}
              onRefused={onRefused}
              onCompanyConfirmed={onCompanyConfirmed}
              onClose={() => dispatch({ type: 'dismissed' })}
            />
          )
        : null}
    </main>
  );
}

export default InviteAckPage;
